import React, {useCallback, useMemo, useRef, useState} from 'react';
import {CBCButton, Icon} from 'shared/helpers';
import {Modal, Dropdown} from 'react-bootstrap';
import {
    CloseEventType,
    Footer,
} from 'components/customer/AdvancedMaterials/Footer';
import {Menu} from 'components/customer/AdvancedMaterials/Menu';
import styled from 'styled-components';
import {Search} from 'components/customer/AdvancedMaterials/Search';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    BrowserType,
    clearAll,
    selectBrowserType,
    selectDoor,
    selectEdge,
    selectHasDoors,
    selectMaterial,
    selectMaterialZoom,
    selectMenu,
    thicknessSelect,
    selectThicknesses,
    selectThickness,
    clearFilters,
} from 'components/customer/AdvancedMaterials/store/materialSlice';
import {shallowEqual, useSelector} from 'react-redux';
import {Material} from 'components/customer/AdvancedMaterials/entity/Material';
import {Body as Content} from 'components/customer/AdvancedMaterials/Body';
import {BodyContent} from 'components/customer/AdvancedMaterials/BodyContent';
import {Menu as MenuI} from 'components/customer/AdvancedMaterials/entity/Menu';
import {Doors} from 'components/customer/AdvancedMaterials/Doors';
import {MaterialAndDoorSelection} from 'components/customer/AdvancedMaterials/CurrentSelections/MaterialAndDoorSelection';
import {Door} from 'components/customer/Materials/entity';
import {Title} from 'components/customer/AdvancedMaterials/Title';
import {Finishes} from 'components/customer/AdvancedMaterials/BrandAndFinish/Finish';
import {Materials as BTMMaterials} from 'components/customer/AdvancedMaterials/Materials';
import Materials from 'components/customer/AdvancedMaterials/Materials/withMaterial';
import Edges from 'components/customer/AdvancedMaterials/Materials/withEdge';
import {BenchtopSelection} from 'components/customer/AdvancedMaterials/CurrentSelections/BenchtopSelection';
import {MiniRadio} from 'shared/components/Forms/MiniRadio';
import {Filters} from 'components/customer/AdvancedMaterials/entity/Filters';
interface AdvancedMaterialsInterface {
    onShow?: () => void;
    onApply?: (
        material: Material,
        door?: Door,
        edge?: Material
    ) => void | boolean;
    onClose?: () => void;
    onCancel?: () => void;
    checkBeforeClose?: boolean;
    className?: string;
    disableApply?: boolean;
    $width?: number;
    buttonText?: string;
    buttonRef?: React.RefObject<HTMLButtonElement>;
    isFetchingMaterialFinishes?: boolean;
    isFetchingEdgeFinishes?: boolean;
}

const AdvancedMaterials = ({
    onShow,
    onApply,
    onCancel,
    className,
    checkBeforeClose = false,
    disableApply = false,
    buttonText = 'Browse Colours',
    buttonRef,
    isFetchingMaterialFinishes = false,
    isFetchingEdgeFinishes = false,
}: AdvancedMaterialsInterface) => {
    const container = useRef<HTMLDivElement>();
    const thicknesses = useAppSelector(selectThicknesses, shallowEqual);
    const [show, setShow] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);

    const selectedMaterial = useAppSelector(selectMaterial, shallowEqual);
    const selectedDoor = useAppSelector(selectDoor, shallowEqual);
    const selectedEdge = useAppSelector(selectEdge, shallowEqual);
    const colorBrowserType = useAppSelector(selectBrowserType);
    const selectedMenu = useAppSelector(selectMenu);
    const hasDoors = useAppSelector(selectHasDoors);
    const [showThicknessDropdown, setShowThicknessDropdown] = useState(false);
    const selectedThickness = useAppSelector(selectThickness, shallowEqual);
    const dispatch = useAppDispatch();

    const materialZoom = useSelector(selectMaterialZoom);

    const handleShow = useCallback(() => {
        if (typeof onShow == 'function') {
            onShow();
        }
        dispatch(clearFilters(Filters.THICKNESS));
        setShow(true);
    }, [onShow]);

    const handleClearAll = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            dispatch(clearFilters(Filters.THICKNESS));
        },
        [selectedMenu]
    );

    const handleThicknessChange = useCallback(
        (thickness: string, event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            dispatch(thicknessSelect(thickness));
        },
        []
    );

    const handleClose = useCallback(
        (event: CloseEventType) => () => {
            if (event == CloseEventType.APPLY && typeof onApply == 'function') {
                // NOTE: Add pass onApply from AdvanedMaterialsBenchtop.tsx and
                // possibly update these callbacks when selected material is updated.
                const closeCheck = onApply(
                    selectedMaterial,
                    selectedDoor,
                    selectedEdge
                );

                if (!checkBeforeClose || closeCheck) {
                    setShow(false);
                    dispatch(clearAll());
                }
            } else if (
                event == CloseEventType.CANCEL &&
                typeof onCancel == 'function'
            ) {
                onCancel();
                setShow(false);
                dispatch(clearAll());
            } else {
                setShow(false);
                dispatch(clearAll());
            }
        },
        [
            selectedMaterial,
            selectedDoor,
            selectedEdge,
            checkBeforeClose,
            onApply,
            onCancel,
        ]
    );

    const showMaterialsAndDoors = useMemo(() => {
        return (
            colorBrowserType != BrowserType.BENCHTOP &&
            ((hasDoors && selectedMenu != MenuI.DOOR_TYPE) || !hasDoors)
        );
    }, [hasDoors, selectedMenu, colorBrowserType]);

    const toggleMobileSidebar = useCallback(() => {
        setShowSidebar((showSidebar) => !showSidebar);
    }, []);

    const handleToggleThicknessDropdown = useCallback((isOpen: boolean) => {
        setShowThicknessDropdown(isOpen);
    }, []);

    return (
        <>
            <MaterialButton
                buttonRef={buttonRef}
                $applyMinimum={colorBrowserType != BrowserType.QFP}
                iconName="Button-Edit.svg"
                className={`button-blue simplified-material-button advanced_material_editor ${className}`}
                onClick={handleShow}>
                <strong>{buttonText}</strong>
            </MaterialButton>

            <CBCModal
                show={show}
                onHide={handleClose(CloseEventType.CANCEL)}
                animation={false}
                backdrop="static">
                <Header>
                    <HeaderMenu
                        onClick={toggleMobileSidebar}
                        iconName={showSidebar ? 'menu_open.svg' : 'menu.svg'}
                        style={{width: '24px'}}
                    />
                    <div>Material Selector</div>
                    <CloseButton
                        iconName="Button-Cancel.svg"
                        onClick={handleClose(CloseEventType.CANCEL)}
                        className="job-button button-light"
                        style={{color: '#8A8080'}}
                    />
                </Header>
                <Body>
                    <Sidebar $showSidebar={showSidebar}>
                        <Menu />
                    </Sidebar>
                    <Container ref={container}>
                        {colorBrowserType === BrowserType.BENCHTOP ? (
                            <BenchtopSelection />
                        ) : showMaterialsAndDoors ? (
                            <MaterialAndDoorSelection />
                        ) : null}
                        <SearchLayout
                            $justification={
                                hasDoors && selectedMenu == MenuI.DOOR_TYPE
                                    ? 'flex-end'
                                    : 'space-between'
                            }
                            $alignment="flex-start"
                            $showTitle={
                                (hasDoors && selectedMenu != MenuI.DOOR_TYPE) ||
                                !hasDoors
                            }
                            $materialZoom={materialZoom}>
                            <Title />

                            <FinishesAndSearch>
                                {colorBrowserType == BrowserType.BENCHTOP ? (
                                    <Dropdown
                                        show={showThicknessDropdown}
                                        onToggle={handleToggleThicknessDropdown}
                                        onSelect={handleThicknessChange}>
                                        <DropdownToggle
                                            variant="primary"
                                            id="dropdown-thickness">
                                            <div>
                                                {selectedThickness &&
                                                selectedThickness.length > 0
                                                    ? selectedThickness
                                                          .map(
                                                              (
                                                                  thickness: string
                                                              ) =>
                                                                  `${thickness}mm`
                                                          )
                                                          .join(',')
                                                    : 'Select Thickness'}
                                            </div>
                                            <Icon iconName="Chevron-down.svg" />
                                        </DropdownToggle>

                                        <DropdownMenu>
                                            <Dropdown.Item as="div">
                                                <Button
                                                    onClick={handleClearAll}
                                                    disabled={
                                                        !!selectedThickness &&
                                                        !(
                                                            selectedThickness.length >
                                                            0
                                                        )
                                                    }>
                                                    Clear all filters
                                                </Button>
                                            </Dropdown.Item>
                                            {thicknesses
                                                ? thicknesses
                                                      .filter(
                                                          (thickness) =>
                                                              thickness.name !=
                                                              ''
                                                      )
                                                      .sort((a, b) => {
                                                          const thicknessA =
                                                              parseInt(a.name);
                                                          const thicknessB =
                                                              parseInt(b.name);
                                                          return (
                                                              thicknessB -
                                                              thicknessA
                                                          );
                                                      })
                                                      .map((thickness) => {
                                                          return (
                                                              <Dropdown.Item
                                                                  as="div"
                                                                  key={
                                                                      thickness.id
                                                                  }
                                                                  eventKey={
                                                                      thickness.id
                                                                  }>
                                                                  <MiniRadio
                                                                      key={
                                                                          thickness.id
                                                                      }
                                                                      $checked={
                                                                          thickness.selected
                                                                      }>
                                                                      {
                                                                          thickness.name
                                                                      }
                                                                      <input type="checkbox" />
                                                                  </MiniRadio>
                                                              </Dropdown.Item>
                                                          );
                                                      })
                                                : null}
                                        </DropdownMenu>
                                    </Dropdown>
                                ) : null}
                                <FinishesWrapper>
                                    <Finishes
                                        isLoading={
                                            selectedMenu == MenuI.EDGE_MATERIAL
                                                ? isFetchingEdgeFinishes
                                                : isFetchingMaterialFinishes
                                        }
                                    />
                                </FinishesWrapper>
                                <Search />
                            </FinishesAndSearch>
                        </SearchLayout>

                        <Content>
                            <BodyContent
                                eventKey={
                                    hasDoors
                                        ? [MenuI.MATERIAL, MenuI.THICKNESSES]
                                        : [
                                              MenuI.MATERIAL,
                                              MenuI.THICKNESSES,
                                              MenuI.DOOR_TYPE,
                                          ]
                                }>
                                {colorBrowserType == BrowserType.BENCHTOP ? (
                                    <BTMMaterials />
                                ) : (
                                    <Materials />
                                )}
                            </BodyContent>
                            <BodyContent
                                eventKey={hasDoors ? MenuI.DOOR_TYPE : []}>
                                <Doors container={container} />
                            </BodyContent>
                            <BodyContent
                                eventKey={
                                    hasDoors
                                        ? MenuI.EDGE_MATERIAL
                                        : [MenuI.EDGE_MATERIAL, MenuI.DOOR_TYPE]
                                }>
                                <Edges />
                            </BodyContent>
                        </Content>
                    </Container>
                </Body>
                <Footer onClose={handleClose} disableApply={disableApply} />
            </CBCModal>
        </>
    );
};

export default AdvancedMaterials;

const HeaderMenu = styled((props) => <Icon {...props} />)`
    display: none;

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        display: block;
    }
`;

const CBCModal = styled(Modal)`
    background: rgba(255, 255, 255, 0.5);

    .modal-dialog {
        width: 70vw;
        max-width: initial;
        height: calc(100% - 50px);
        margin: 25px auto;
    }

    .modal-content {
        border-radius: 20px;
        box-shadow: 0px 0px 10px 1px #616161bf;
        border: 0;
        height: 100%;
    }

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        overflow-y: hidden !important;

        .modal-dialog {
            width: 100vw;
            height: 100vh;
            margin: 0;
        }

        .modal-content {
            border-radius: 0 !important;
            height: 100%;
        }
    }
`;

export const Header = styled(Modal.Header)`
    background: rgb(var(--primary_colour));
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 10px;

    > div {
        text-align: center;
        width: 100%;
        font-weight: 900;
        color: white;
        font-size: 1em;
    }

    .close {
        display: none;
    }

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        border-radius: 0 !important;
    }
`;

const Sidebar = styled.div<{$showSidebar: boolean}>`
    width: 300px;
    background: #e6e7e9;
    padding: 0;

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        position: absolute;
        top: 0;
        left: -300px;
        bottom: 0;
        background: white;
        z-index: 4;
        box-shadow: 0 0 4px 1px gray;

        ${({$showSidebar}) => {
            if ($showSidebar) {
                return `left: 0;`;
            }
        }}
    }
`;

const Container = styled.div`
    padding: 15px;
    flex: 1;
    display: flex;
    flex-direction: column;

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        padding: 0;
    }
`;

const Body = styled(Modal.Body)`
    padding: 0;
    display: flex;
    height: calc(100% - 45px - 71px);
`;

const SearchLayout = styled.div<{
    $showTitle?: boolean;
    $alignment: string;
    $justification: string;
    $materialZoom?: boolean;
}>`
    display: flex;
    justify-content: ${({$justification}) => $justification};
    align-items: ${({$alignment}) => $alignment};
    margin-top: ${({$materialZoom}) => ($materialZoom ? '400px' : '0px')};
    ${({$showTitle = false}) => {
        if ($showTitle) {
            return `border-top: 2px solid rgb(var(--secondary_colour));`;
        }
    }}

    > h1 {
        margin: 0;
        background: rgb(var(--secondary_colour));
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 15px !important;
        border-bottom-left-radius: 0;
        padding: 0 clamp(10px, 3vw, 50px);
        font-weight: 900;
        min-height: 30px;
        margin-top: -2px;
    }

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        border: 0;

        > h1 {
            display: none;
        }
    }
`;

const FinishesAndSearch = styled.div`
    display: flex;
    padding-top: 10px;
    margin-left: 10px;

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        padding: 5px 5px 0;
        gap: 5px;

        > div {
            width: calc(50% - 2px) !important;
            margin: 0 !important;

            > button {
                width: 100%;
            }
        }
    }
`;

// NOTE: Jest is weird sometimes and cannot find component that
// is being imported. So, we need to use this workaround.
export const MaterialButton = styled((props) => <CBCButton {...props} />)`
    width: 100% !important;
    font-size: 1em !important;
    min-width: ${({$applyMinimum}) => ($applyMinimum ? '150px' : 'initial')};

    > img {
        width: 29px !important;
    }
`;

// NOTE: Jest is weird sometimes and cannot find component that
// is being imported. So, we need to use this workaround.
export const CloseButton = styled((props) => <CBCButton {...props} />)`
    color: rgb(138, 128, 128);
    background: transparent;
    border: 0 !important;
    width: 20px;
    height: 20px;
    padding: 0;
    margin: 0;

    img {
        width: 20px !important;
        margin: 0 !important;
    }
`;

const DropdownToggle = styled(Dropdown.Toggle)`
    background: white !important;
    border: 2px solid rgb(var(--primary_colour)) !important;
    border-radius: 8px;
    color: gray !important;
    font-size: 1em;
    height: 38px;
    font-weight: 500;
    display: flex;
    align-items: center;
    width: 10vw;

    &:after {
        display: none;
    }

    > div {
        flex: 1;
        text-align: left;
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    > img {
        width: 15px;
        margin-left: 15px;
    }

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        width: 40vw;
    }
`;

const DropdownMenu = styled(Dropdown.Menu)`
    right: 0 !important;
    left: 0 !important;
    background: rgb(218, 222, 223);
    border: 0;
    box-shadow: 0 0 4px 0 #b9b5b5;
    min-width: max-content !important;
    max-height: 300px;
    overflow-y: auto;

    > div {
        font-size: 0.8em;
        font-weight: 500;
        color: #6b6f70 !important;
        padding: 0.01rem 1.5rem;
        white-space: initial;
        line-height: 1em;
        padding-top: 5px;
        padding-bottom: 5px;
        cursor: pointer;
    }

    > div:hover,
    a:focus {
        background: #c5c9ca;
    }
`;

const FinishesWrapper = styled.div`
    margin-left: 15px;
`;

const Button = styled.button`
    border: 0px;
    background: rgb(var(--secondary_colour));
    color: white;
    border-radius: 6px;
    font-weight: 500;
    padding: 4px 5px;

    &:hover {
        background: rgb(var(--primary_colour));
    }

    &:disabled {
        background: rgb(var(--secondary_colour));
        opacity: 0.5;
    }
`;
